import { useEffect } from 'react'
import { createPortal } from 'react-dom'
import { useAppState } from '@/core/state/react'
import { appActions } from '@/core/state/app'

/** MM:SS, or --:-- for a missing value. App.vue's formatTime. */
export function formatTime(seconds: number | null | undefined): string {
  if (seconds === null || seconds === undefined) return '--:--'
  const mins = Math.floor(seconds / 60)
  const secs = seconds % 60
  return `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`
}

/**
 * App.vue's persistent <v-overlay v-model="isLoading">, with Est / Remaining /
 * Elapsed ticking once a second while an estimate exists — the watch App.vue
 * used, as one effect keyed on what it actually depends on.
 */
export function LoadingOverlay() {
  const isLoading = useAppState((s) => s.isLoading)
  const message = useAppState((s) => s.loadingMessage)
  const timing = useAppState((s) => s.loadingTiming)
  const hasEstimate = Boolean(timing.estimatedSeconds)

  useEffect(() => {
    if (!isLoading || !hasEstimate) return
    const id = window.setInterval(() => appActions.updateLoadingTiming(), 1000)
    return () => window.clearInterval(id)
  }, [isLoading, hasEstimate])

  if (!isLoading) return null
  return createPortal(
    <div className="scvc-overlay" data-testid="loading-overlay" role="status" aria-live="polite" aria-busy="true">
      <div className="scvc-overlay__scrim" />
      <div className="scvc-overlay__content loading-container">
        {hasEstimate && (
          <div className="loading-timing">
            <div className="scvc-timing-row">
              <div className="timing-item">
                <span className="timing-label">Est:</span>
                <span className="timing-value">{formatTime(timing.estimatedSeconds)}</span>
              </div>
              <span className="scvc-divider timing-divider" />
              <div className="timing-item">
                <span className="timing-label">Remaining:</span>
                <span className="timing-value">{formatTime(Math.max(0, timing.remainingSeconds ?? 0))}</span>
              </div>
              <span className="scvc-divider timing-divider" />
              <div className="timing-item">
                <span className="timing-label">Elapsed:</span>
                <span className="timing-value">{formatTime(timing.elapsedSeconds)}</span>
              </div>
            </div>
          </div>
        )}
        <div className="scvc-spinner" aria-hidden="true" />
        <div className="scvc-loading-message">{message}</div>
      </div>
    </div>,
    document.body,
  )
}
